import { useCameraPinchGesture } from '@euler/app/components/media/camera/hooks/useCameraPinchGesture';
import { MobileWebCaptureButton } from '@euler/app/flows/media/components/MobileWebCaptureButton';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { safeMarkDevFlag } from '@euler/utils';
import { SafeHaptics } from '@euler/utils/haptics.web';
import { usePersistFn } from '@euler/utils/hooks';
import {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import { GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedReaction,
  useSharedValue,
} from 'react-native-reanimated';

const kTest = safeMarkDevFlag(false);

// see web media technologies for more details:
// - https://developer.mozilla.org/en-US/docs/Web/API/Media_Capture_and_Streams_API
// - https://developer.mozilla.org/en-US/docs/Web/API/MediaStream_Recording_API
// - https://developer.mozilla.org/en-US/docs/Web/API/ImageCapture
// more tutorials:
// - https://www.digitalocean.com/community/tutorials/front-and-rear-camera-access-with-javascripts-getusermedia
// - https://dominikschilling.de/notes/ios-access-all-back-cameras-mediadevices-api/
export const MobileWebCameraView = memo(
  ({
    mask,
    onPhotoTaken,
  }: {
    mask?: { width: number; height: number };
    onPhotoTaken: (dataURI: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { width, height } = useWindowDimensions();

    const [testUrl, setTestUrl] = useState<string | null>(null);
    const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>(
      [],
    );
    const [activeCamera, setActiveCamera] = useState<MediaDeviceInfo>();

    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream>();

    const zoom = useSharedValue(0);
    const minZoom = 1;
    const maxZoom = 10;

    const pinchGesture = useCameraPinchGesture({
      enabled: true,
      zoom,
      minZoom,
      maxZoom,
    });

    const stopActiveCamera = useCallback(() => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        if (videoRef.current != null) {
          videoRef.current.srcObject = null;
        }
      }
    }, []);

    const startCamera = useCallback(
      async (device: MediaDeviceInfo) => {
        if (device.deviceId === activeCamera?.deviceId) {
          return;
        }

        stopActiveCamera();

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: device.deviceId },
          },
        });

        streamRef.current = stream;

        if (videoRef.current != null) {
          videoRef.current.srcObject = stream;
        }

        setActiveCamera(device);
      },
      [activeCamera?.deviceId, stopActiveCamera],
    );

    const startDefaultCamera = usePersistFn(async () => {
      try {
        if (videoRef.current == null) return;

        const video = videoRef.current;
        video.removeAttribute('controls');
        video.controls = false;

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            ...{
              zoom: 1.0,
              focalLength: { ideal: 26 }, // Prefer standard focal length
            },
          },
        });

        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const cameras = allDevices.filter(
          device => device.kind === 'videoinput',
        );
        setAvailableCameras(cameras);

        const videoTrack = stream.getVideoTracks()[0];
        const selected = cameras.find(x => x.label === videoTrack.label);
        setActiveCamera(selected);

        streamRef.current = stream;
        video.srcObject = stream;
      } catch (err) {
        console.error('Error accessing the camera:', err);
        alert((err as any).message);
      }
    });

    useEffect(() => {
      void startDefaultCamera();
      return () => {
        stopActiveCamera();
      };
    }, [startDefaultCamera, stopActiveCamera]);

    const onCameraChange = useCallback(
      (e: ChangeEvent<HTMLSelectElement>) => {
        const deviceId = e.target.value;
        const device = availableCameras.find(x => x.deviceId === deviceId);
        if (device == null || device.deviceId === activeCamera?.deviceId) {
          return;
        }
        void startCamera(device);
      },
      [activeCamera?.deviceId, availableCameras, startCamera],
    );

    const onTakePhoto = useCallback(() => {
      SafeHaptics.impact();
      if (videoRef.current == null || mask == null) {
        return;
      }

      const video = videoRef.current;

      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      const dpr = window.devicePixelRatio || 1;

      // Get the video frame
      const videoRect = video.getBoundingClientRect();
      const scaleX = videoWidth / videoRect.width;
      const scaleY = videoHeight / videoRect.height;

      const cropX = (scaleX * (width - mask.width)) / 2;
      const cropY = (scaleY * (height - mask.height)) / 2;
      const cropWidth = mask.width * scaleX;
      const cropHeight = mask.height * scaleY;
      const outputWidth = cropWidth * dpr;
      const outputHeight = cropHeight * dpr;

      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = outputWidth;
      croppedCanvas.height = outputHeight;
      const croppedContext = croppedCanvas.getContext('2d')!;

      // Scale context to account for pixel ratio
      croppedContext.scale(dpr, dpr);

      // Draw the cropped image
      croppedContext.drawImage(
        video,
        cropX,
        cropY,
        cropWidth,
        cropHeight, // Source area from the video
        0,
        0,
        cropWidth,
        cropHeight, // Destination area on the canvas
      );

      const dataURI = croppedCanvas.toDataURL();

      if (kTest) {
        setTestUrl(dataURI);
      } else {
        onPhotoTaken?.(dataURI);
      }
    }, [height, mask, onPhotoTaken, width]);

    const setZoom = useCallback((value: number) => {
      if (videoRef.current?.srcObject == null) return;
      const stream = videoRef.current.srcObject as MediaStream;
      const track = stream.getVideoTracks()[0];
      void track.applyConstraints({ advanced: [{ zoom: value } as any] });
    }, []);

    useAnimatedReaction(
      () => zoom.value,
      (prev, curr) => {
        if (
          prev == null ||
          curr == null ||
          videoRef.current?.srcObject == null
        ) {
          return;
        }
        setZoom(curr);
      },
    );

    return (
      <GestureDetector gesture={pinchGesture}>
        <Animated.View
          style={StyleSheet.absoluteFill}
          css={`
            background-color: black;
          `}
        >
          {mask != null && testUrl != null ? (
            <img
              src={testUrl}
              style={{
                position: 'absolute',
                left: (width - mask.width) / 2,
                top: 60,
                width: mask.width,
                height: mask.height,
                zIndex: 20,
              }}
            />
          ) : null}
          <video
            ref={videoRef}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              objectFit: 'fill',
            }}
            controls={false}
            muted
            autoPlay
            playsInline
          />
          {mask != null && (
            <CameraCropArea width={width} height={height} mask={mask} />
          )}
          {availableCameras?.length ? (
            <Center
              style={{
                position: 'absolute',
                left: 0,
                top: rpx(20),
                right: 0,
                zIndex: 10,
              }}
            >
              <select onChange={onCameraChange}>
                {availableCameras.map(camera => (
                  <option
                    key={camera.deviceId}
                    value={camera.deviceId}
                    selected={activeCamera?.deviceId === camera.deviceId}
                  >
                    {camera.label}
                  </option>
                ))}
              </select>
            </Center>
          ) : null}
          {activeCamera != null && (
            <Center
              style={{
                position: 'absolute',
                left: 0,
                bottom: rpx(20),
                right: 0,
                zIndex: 10,
              }}
            >
              <MobileWebCaptureButton
                zoom={zoom}
                minZoom={minZoom}
                maxZoom={maxZoom}
                onTakePhoto={onTakePhoto}
              />
            </Center>
          )}
        </Animated.View>
      </GestureDetector>
    );
  },
);

const CameraCropArea = memo(
  ({
    width,
    height,
    mask,
  }: {
    width: number;
    height: number;
    mask: { width: number; height: number };
  }) => {
    return (
      <svg
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          pointerEvents: 'none',
        }}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
      >
        <defs>
          <mask id="crop-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={(width - mask.width) / 2}
              y={(height - mask.height) / 2}
              width={mask.width}
              height={mask.height}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#crop-mask)"
        />
      </svg>
    );
  },
);
